import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { useBillingStatus } from "@/features/billing/hooks/useBilling";

export const Route = createFileRoute("/_authenticated/billing/success")({
  component: BillingSuccessPage,
});

function BillingSuccessPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useBillingStatus();

  return (
    <div className="mx-auto max-w-xl py-12">
      <div className="rounded-2xl border border-border bg-surface p-8 shadow-xl relative overflow-hidden">
        {/* Decorative subtle background gradient */}
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-indigo/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-cyan/10 blur-3xl pointer-events-none" />

        <div className="flex flex-col items-center text-center">
          {/* Animated/Styled success check */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10 text-success mb-6">
            <CheckCircle2 size={36} className="animate-bounce" />
          </div>

          <h1 className="text-3xl font-extrabold text-heading tracking-tight mb-3">
            {t("billing.successTitle")}
          </h1>

          <p className="text-muted mb-8 max-w-md">{t("billing.successSubtitle")}</p>

          {/* Active plan status card */}
          <div className="w-full rounded-xl bg-elevated/50 border border-border/50 p-5 mb-8 text-left">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                {t("billing.successBillingStatus")}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-indigo font-semibold">
                <Sparkles size={12} />
                {t("billing.active")}
              </span>
            </div>

            {isLoading ? (
              <div className="h-6 w-24 animate-pulse rounded bg-border" />
            ) : (
              <div className="text-lg font-bold text-heading">{data?.plan.name ?? "Pro"}</div>
            )}

            {!isLoading && data?.plan.billing_email && (
              <div className="text-xs text-muted mt-1">{data.plan.billing_email}</div>
            )}
          </div>

          {/* Actions */}
          <Link
            to="/apps"
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo py-3 px-4 font-semibold text-white transition-all hover:bg-indigo-bright shadow-lg shadow-indigo/20 hover:shadow-indigo/30"
          >
            {t("billing.goToApps")}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
